import React from "react";
import { withTranslation } from "react-i18next";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/Table';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Spinner } from '../../../components/ui/Spinner';
import AppIcon from '../../../components/AppIcon';
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setPrevUrl } from "../../../store/redux/actions/userActionCreator";
import { Branch, Department, Roles, UserView } from "../../../models/general";
import { Roles as AuthRoles } from '../../../components/roles'
import { getBranches, getCompany, getDepartments } from "../../../api/General/Company";
import { deleteUser, getUser, restoreUser, FetchUsers } from "../../../api/General/account";
import Pagination from "../../../components/Pagination";
import { Users } from 'lucide-react';
import { PageLayout } from '../../../components/ui/PageLayout';
import ResetPasswordDialog from './ResetPasswordDialog';
import RestrictedWriteNotice from '../../../components/subscription/RestrictedWriteNotice';
import { RESTRICTED_WRITE_REASON_ID } from '../../../hooks/useWriteAccess';
import { withWriteAccess, type WithWriteAccessProps } from '../../../components/subscription/withWriteAccess';
import ManagedElsewhereNotice from '../../../components/capabilities/ManagedElsewhereNotice';
import { withCompanyCapabilities, type WithCompanyCapabilitiesProps } from '../../../components/capabilities/withCompanyCapabilities';
import { formatDate } from '../../../utils/dateFormatter';
import { HR_ONLY_ROLES } from '../../../components/layout/navigationModules';

/*
  EXIT-DATE LIFECYCLE (docs/exit-date-lifecycle-spec.md §3.6 / D7).

  The list used to be a binary: one checkbox labelled Active / Archived. That was never the real
  shape of the data — termination archives nobody, so somebody who left the company last March sat
  in "Active" next to everyone still working, indistinguishable. Four segments name the four states
  the server now derives, and the row chip answers the question the old list could not: WHEN.

  Why three of the four are filtered client-side: the server's `lifecycle=all` returns every
  non-archived employee with a `lifecycleState` already stamped on each row, so Current / Leaving /
  Former are one request and an instant segment switch instead of three round trips. Archived is a
  genuinely different query (`archived=true`) and stays a separate fetch, which is also why its count
  only appears once you have opened it — showing a number we have not fetched would be a guess.
*/
type LifecycleTab = 'current' | 'leaving' | 'former' | 'archived';

/*
  OWNER DECISION 2026-09-14 — the Archived segment is HR ONLY.

  Archived employees are HR-owned history (CLAUDE.md Role Model: Admin administers the company, it is
  not an HR role, and a Manager's scope is their own team). Since CO-692 (1b7dc9ec) only an Hr caller
  can open an archived profile, so for everyone else the segment was a list whose every row led to
  "Employee profile not found" — an affordance that could only dead-end. Removed rather than disabled:
  a disabled radio is still announced to assistive tech as a choice this user has, which is the wrong
  statement. `HR_ONLY_ROLES` is the same constant that gates /payroll-reports.
*/
const ALL_LIFECYCLE_TABS: readonly LifecycleTab[] = ['current', 'leaving', 'former', 'archived'];
const NON_HR_LIFECYCLE_TABS: readonly LifecycleTab[] = ['current', 'leaving', 'former'];
// CO-722 (owner decision 2026-09-15) — an Admin (not HR) may not reset the password of an HR, Admin or
// Operator user. Mirrors AccountService.AdminBlockedFromPrivilegedTargetAsync, which is the real gate; this
// only hides the button so the Admin is not walked into a 403.
const PRIVILEGED_RESET_TARGET_ROLES: readonly string[] = [Roles.Hr, Roles.Admin, Roles.Operator];
const adminBarredFromReset = (callerRole: unknown, target: { role?: string; roles?: string[] }): boolean =>
    callerRole === Roles.Admin
    && [...(target.roles ?? []), target.role ?? ''].some((r) => PRIVILEGED_RESET_TARGET_ROLES.includes(r));

const isHrRole = (role: unknown): boolean =>
    typeof role === 'string' && HR_ONLY_ROLES.includes(role);

type Props = Readonly<{
    match?: any,
    t: any,
    setPrevUrl?: any,
    companyPrimaryColor?: any,
    companyName?: any,
    role?: any,
    company?: any,
    rtl?: boolean
    userId?: string,
    navigate?: (path: string) => void; // injected via withNavigation HOC
    // CO-24 — injected by withWriteAccess. /api/Account/AddUsers and /api/User are NOT on the
    // server's RestrictedWriteAllowlist, so every employee mutation 403s while Restricted/Dormant.
}> & WithWriteAccessProps & WithCompanyCapabilitiesProps;

type State = {
    data: any[];
    selectedPageIndex: number;
    count: number;
    openExpense: boolean;
    openBenefit: boolean;
    users: UserView[];
    allusers: UserView[];
    columns: any[];
    sortKey: 'fullName' | 'managerName' | 'title' | 'department' | null;
    sortDir: 'asc' | 'desc';
    showFilters: boolean;
    isArchivedChecked: boolean;
    /** Which lifecycle segment is selected. `archived` is the one that changes the server query. */
    lifecycleTab: LifecycleTab;
    /** Archived count, known only after the Archived segment has actually been fetched. */
    archivedCount: number | null;
    loading: boolean;
    items: UserView[];
    offset: number;
    error?: string;
    companyName: string;
    roles: string[];
    branches: Branch[];
    departments: Department[];
    selectedRole: string;
    selectedBranch: string;
    selectedDepartment: string;
    selectedStatus: string;
    managers: any[];
    filter: {
        employeeId: string;
        fullName: string;
        email: string;
        company: string;
        branch: string;
        department: string;
        phoneNumber: string;
        country: string;
        role: string;
        positions: string[];
        isarchived: boolean
    };
    searchTerm: string;
    actionLoadingIds: string[];
    resetTarget: { id: string; name: string } | null;
};

class EmployeesGeneral extends React.Component<Props, State> {

    /**
     * Which segments this viewer may choose between. Derived per render rather than filtered at the
     * point of use so the selected value, the count badges and the wrap behaviour all read off one
     * list — a control whose selected value is not in its own option list is the bug this avoids.
     */
    allowedLifecycleTabs(): readonly LifecycleTab[] {
        return isHrRole(this.props.role) ? ALL_LIFECYCLE_TABS : NON_HR_LIFECYCLE_TABS;
    }

    /**
     * `?lifecycle=` deep link, validated against what this viewer is allowed to open. A non-HR user
     * who lands on `?lifecycle=archived` gets Current — fail CLOSED, which also means a role that has
     * not hydrated yet resolves to Current rather than to a segment the server would refuse anyway.
     */
    static initialLifecycleTab(props: Props): LifecycleTab {
        const requested = new URLSearchParams(window.location.search).get('lifecycle')?.toLowerCase();
        const allowed: readonly LifecycleTab[] = isHrRole(props.role) ? ALL_LIFECYCLE_TABS : NON_HR_LIFECYCLE_TABS;
        return allowed.includes(requested as LifecycleTab) ? (requested as LifecycleTab) : 'current';
    }

    constructor(props: Props) {
        super(props);
        const initialTab = EmployeesGeneral.initialLifecycleTab(props);
        this.state = {
            data: [],
            selectedPageIndex: 0,
            openExpense: false,
            openBenefit: false,
            offset: 0,
            count: 10,
            items: [],
            users: [],
            allusers: [],
            roles: [],
            branches: [],
            departments: [],
            loading: false,
            showFilters: false,
            isArchivedChecked: initialTab === 'archived',
            lifecycleTab: initialTab,
            archivedCount: null,
            companyName: '',
            selectedRole: '',
            selectedBranch: '',
            selectedDepartment: '',
            selectedStatus: "",
            managers: [{
                userId: "",
                managerName: "",
            }],
            filter: {
                employeeId: "",
                fullName: "",
                email: "",
                company: "",
                branch: "",
                department: "",
                phoneNumber: "",
                country: "",
                role: "",
                positions: [],
                isarchived: false,
            },
            columns: [],
            sortKey: null,
            sortDir: 'asc',
            searchTerm: "",
            actionLoadingIds: [],
            resetTarget: null,
        };
    }

    toggleSort = (key: NonNullable<State['sortKey']>) => {
        this.setState(prev => {
            if (prev.sortKey === key) {
                return { sortKey: key, sortDir: prev.sortDir === 'asc' ? 'desc' : 'asc' } as Pick<State, 'sortKey' | 'sortDir'>;
            }
            return { sortKey: key, sortDir: 'asc' } as Pick<State, 'sortKey' | 'sortDir'>;
        }, this.applyFilters);
    }
    currentURL = window.location.href;

    componentDidUpdate(prevProps: Props) {
        // The role arrives from persisted redux state and can change under a session swap. If the
        // viewer loses HR while Archived is open, drop them back to Current rather than leave a
        // segment selected that is no longer offered (and whose server query now refuses them).
        if (prevProps.role !== this.props.role
            && this.state.lifecycleTab === 'archived'
            && !isHrRole(this.props.role)) {
            this.selectLifecycleTab('current');
        }
    }

    componentDidMount() {
        this.fetchData()
        console.log(this.currentURL);
        console.log("RTL OR LTR?: ", this.props.rtl ? this.props.rtl : this.props.rtl);
    }

    fetchData = async () => {
        const { t } = this.props
        const checked = this.state.isArchivedChecked
        this.setState({ loading: true });
        const id = this.props.company?.id ?? 0;
        if (id || AuthRoles.ADMIN.includes(this.props.role)) {
            try {
                if (id) {
                    const company = (await getCompany(id)).payload;
                    this.setState({ companyName: company.name })
                }
                // Unified users fetch (Company scope). The archived segment is its own server query;
                // every other segment comes out of ONE `lifecycle=all` fetch and is split client-side
                // on the server-derived `lifecycleState`, so switching segments costs nothing.
                const usersResult = await FetchUsers('Company', id, checked, checked ? undefined : 'all');
                const users = usersResult.payload;
                if (checked) this.setState({ archivedCount: users.length });
                const roles = { payload: ["Clocking", "Chief", "Hr", "Admin"] };
                // Build managers mapping from payload (ManagerIds + ManagerNames aligned by index)
                const managers = users.map((u: any) => ({
                    userId: u.id,
                    managerName: (u.managerNames ?? [])
                }));
                this.setState({
                    loading: false,
                    users: users,
                    items: [],
                    roles: roles.payload,
                    managers: managers
                }, this.applyFilters);
                const branches = await getBranches(id)
                this.setState({ branches: branches.payload });
                const departments = await getDepartments(id)
                this.setState({ departments: departments.payload });
            } catch (error: any) {
                if (error && error.errors && error.errors.length > 0 && error.errors[0] && error.errors[0].description) {
                    alert(error.errors[0].description);
                }
                this.setState({ loading: false });
            }
        } else {
            this.setState({
                loading: false,
                error: t("User does not have a valid company"),
            })
        }
    };


    // Removed custom styled table components in favor of standardized table utility classes (.table-standard etc.)
    onPageChange = (index: number): void => {
        this.setState({
            selectedPageIndex: index,
            offset: (index ?? 1) * this.state.count
        })
    }
    filterDataGlobal(value: string) {
        this.setState({ searchTerm: value }, this.applyFilters);
    }

    updateFilter = (key: keyof State['filter'], value: string | boolean) => {
        this.setState(prev => ({ filter: { ...prev.filter, [key]: value } }), this.applyFilters);
    }

    /**
     * Only crossing the archived boundary costs a request — the other three segments are already in
     * `users` and are split on the row's own `lifecycleState`.
     */
    selectLifecycleTab = (tab: LifecycleTab) => {
        // Archived is HR-only (owner 2026-09-14). Guarded here as well as in the render so no code
        // path — a stale deep link, a future caller — can put a non-HR viewer on a query the server
        // now refuses.
        if (!this.allowedLifecycleTabs().includes(tab)) tab = 'current';
        const wasArchived = this.state.lifecycleTab === 'archived';
        const isArchived = tab === 'archived';
        this.setState({ lifecycleTab: tab, isArchivedChecked: isArchived }, () => {
            if (wasArchived !== isArchived) this.fetchData();
            else this.applyFilters();
        });
    }

    /**
     * The state a row is in. `lifecycleState` is the server's derived answer and is authoritative;
     * the employmentStatus fallback only matters against an older payload that predates the field.
     */
    lifecycleOf = (u: UserView): LifecycleTab => {
        if (u.isArchived) return 'archived';
        switch (u.lifecycleState) {
            case 'Leaving': return 'leaving';
            case 'Left': return 'former';
            case 'Archived': return 'archived';
            case 'Active': return 'current';
            default:
                return (u.employmentStatus === 'Terminated' || u.employmentStatus === 'Resigned') ? 'former' : 'current';
        }
    }

    clearFilters = () => {
        this.setState({
            filter: { employeeId: '', fullName: '', email: '', company: '', branch: '', department: '', phoneNumber: '', country: '', role: '', positions: [], isarchived: false },
            searchTerm: '',
            isArchivedChecked: false,
            lifecycleTab: 'current',
        }, this.applyFilters);
        this.fetchData();
    }

    applyFilters = () => {
        const { users, filter, searchTerm, isArchivedChecked, lifecycleTab } = this.state;
        const term = searchTerm.trim().toLowerCase();
        // When archived toggle is on, users list already contains only archived users (server filtered)
        let filtered = isArchivedChecked ? [...users] : users.filter(u => (u.isArchived || false) === isArchivedChecked);
        // Current deliberately keeps LEAVING people in the list: they still work here, and hiding
        // someone on their notice period is exactly the mistake this feature exists to stop.
        if (!isArchivedChecked) {
            filtered = lifecycleTab === 'current'
                ? filtered.filter(u => this.lifecycleOf(u) !== 'former')
                : filtered.filter(u => this.lifecycleOf(u) === lifecycleTab);
        }
        if (filter.role) filtered = filtered.filter(u => (u.role || '').toLowerCase() === filter.role.toLowerCase());
        if (filter.branch) filtered = filtered.filter(u => (u.branch?.id + '') === filter.branch);
        if (filter.department) filtered = filtered.filter(u => (u.department?.id + '') === filter.department);
        if (filter.positions.length > 0) filtered = filtered.filter(u => filter.positions.includes(u.title || ''));
        if (term) {
            filtered = filtered.filter(u => (
                u.userName?.toLowerCase().includes(term) ||
                u.employeeId?.toLowerCase().includes(term) ||
                u.company?.name?.toLowerCase().includes(term) ||
                u.email?.toLowerCase().includes(term) ||
                u.country?.name?.toLowerCase().includes(term) ||
                u.fullName?.toLowerCase().includes(term) ||
                u.phoneNumber?.toLowerCase().includes(term) ||
                u.role?.toLowerCase().includes(term) ||
                u.branch?.name?.toLowerCase().includes(term) ||
                u.department?.name?.toLowerCase().includes(term)
            ));
        }
        const { sortKey, sortDir, managers } = this.state;
        if (sortKey) {
            const getSortValue = (u: any): string => {
                switch (sortKey) {
                    case 'fullName': return (u.fullName || '').toLowerCase();
                    case 'title': return (u.title || '').toLowerCase();
                    case 'department': return (u.department?.name || '').toLowerCase();
                    case 'managerName': {
                        const mgr = managers.find(m => m.userId === u.id);
                        const names = mgr?.managerName;
                        return Array.isArray(names) ? names.join(', ').toLowerCase() : String(names || '').toLowerCase();
                    }
                }
                return '';
            };
            filtered = [...filtered].sort((a, b) => {
                const av = getSortValue(a);
                const bv = getSortValue(b);
                if (av < bv) return sortDir === 'asc' ? -1 : 1;
                if (av > bv) return sortDir === 'asc' ? 1 : -1;
                return 0;
            });
        }
        this.setState({
            items: filtered,
            selectedPageIndex: 0,
            offset: 0,
        });
    }


    render() {
        const checked = this.state.isArchivedChecked
        const { t } = this.props
        const visible = this.state.items;
        const total = this.state.users.length;
        const { loading, actionLoadingIds } = this.state as any;
        const isActionLoading = (id: string) => actionLoadingIds.includes(id);
        // Counts over the whole non-archived payload, not over the visible page — a segment labelled
        // with the number of rows currently on screen would change every time you typed in Search.
        const lifecycleCounts = { current: 0, leaving: 0, former: 0 };
        if (!checked) {
            for (const u of this.state.users) {
                const state = this.lifecycleOf(u);
                if (state === 'former') lifecycleCounts.former++;
                else if (state === 'leaving') { lifecycleCounts.leaving++; lifecycleCounts.current++; }
                else if (state === 'current') lifecycleCounts.current++;
            }
        }
        // Capability wins over write-access: a denial here is permanent, so the action is hidden rather than disabled.
        const canAuthorEmployees = this.props.companyCapabilities.loaded && this.props.companyCapabilities.can('canAuthorEmployees');
        return (
            <PageLayout
                title={t('Employees')}
                description={`${t('Showing')} ${visible.length} / ${total}`}
                icon={Users}
                accentColor="amber"
                maxWidth="1600px"
            >
                {loading && <div className="h-0.5 w-full bg-primary/20"><div className="h-full bg-primary animate-pulse w-1/2" /></div>}
                {/* CO-24 — restricted tenants cannot add, archive or restore employees. */}
                <RestrictedWriteNotice className="mb-4" />
                <ManagedElsewhereNotice capability="canAuthorEmployees" className="mb-4" />
                <section className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-center gap-2 w-full">
                        <div className="relative flex-1">
                            <Input
                                placeholder={t('Search Employees')}
                                disabled={loading}
                                value={this.state.searchTerm}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.filterDataGlobal(e.target.value)}
                            />
                            <AppIcon name="Search" size={18} className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                        </div>
                        <Button variant={this.state.showFilters ? 'default' : 'outline'} iconName="Filter" disabled={loading} onClick={() => this.setState(s => ({ showFilters: !s.showFilters }))}>{t('Filters')}</Button>
                        <Button variant="ghost" iconName="X" disabled={loading} onClick={this.clearFilters}>{t('Clear')}</Button>
                        {/*
                          🔴 CO-24 REPAIR — DO NOT GATE THE "Send Notification" BUTTON BELOW.
                          It navigates to the on-demand notification composer, which posts to
                          `/api/Notification/SendOnDemandNotification`. `/api/Notification` IS on
                          the server's RestrictedWriteAllowlist ("the bell still rings"), so the
                          write SUCCEEDS while Restricted/Dormant. The first CO-24 sweep disabled
                          it anyway — removing a capability the server honours, which is the exact
                          inversion CO-39 was fixed to prevent. Gate against the allowlist, never
                          against "it looks like a write".
                        */}
                        {(this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button
                                disabled={loading}
                                variant="default"
                                iconName="Send"
                                onClick={() => {
                                    this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/send-notification`);
                                }}
                            >{t('Send Notification')}</Button>
                        )}
                        {canAuthorEmployees && (this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button disabled={loading || this.props.writeAccess.blocked} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} variant="outline" iconName="Plus" onClick={() => {
                                if (!canAuthorEmployees) return;
                                this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/add-multiple`);
                            }}>{t('Add multiple employees')}</Button>
                        )}
                        {canAuthorEmployees && (this.props.role === Roles.Admin || this.props.role === Roles.Hr) && (
                            <Button
                                disabled={loading || this.props.writeAccess.blocked}
                                {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})}
                                variant="default"
                                iconName="Plus"
                                onClick={() => {
                                    if (!canAuthorEmployees) return;
                                    this.props.navigate?.(`${this.props.companyName ? '/' + this.props.companyName : ''}/users/add`);
                                }}
                            >{t('Add employee')}</Button>
                        )}
                    </div>
                    {/*
                      Lifecycle segments. Outside the collapsible Filters panel on purpose: this is not
                      a refinement of the list, it is WHICH list — and burying "who has left" behind a
                      disclosure is how the old Archived checkbox stayed invisible for so long.
                      Same segmented vocabulary as the overtime and shift-pay queues.
                    */}
                    <div
                        role="radiogroup"
                        aria-label={t('employees:lifecycle.filterLabel')}
                        data-testid="lifecycle-segments"
                        className="inline-flex flex-wrap rounded-md border border-border bg-card p-0.5"
                    >
                        {this.allowedLifecycleTabs().map((tab) => {
                            const active = this.state.lifecycleTab === tab;
                            const label = tab === 'current' ? t('employees:lifecycle.current')
                                : tab === 'leaving' ? t('employees:lifecycle.leaving')
                                    : tab === 'former' ? t('employees:lifecycle.former')
                                        : t('employees:lifecycle.archived');
                            const count = tab === 'archived'
                                ? this.state.archivedCount
                                : (checked ? null : lifecycleCounts[tab]);
                            return (
                                <button
                                    key={tab}
                                    type="button"
                                    role="radio"
                                    aria-checked={active}
                                    disabled={loading}
                                    onClick={() => this.selectLifecycleTab(tab)}
                                    data-testid={`lifecycle-segment-${tab}`}
                                    className={`rounded px-3 py-1.5 text-xs font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:cursor-not-allowed disabled:opacity-50 ${active ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground'}`}
                                >
                                    {label}
                                    {count !== null && count !== undefined && (
                                        <span className="ms-1.5 tabular-nums opacity-70">{count}</span>
                                    )}
                                </button>
                            );
                        })}
                    </div>
                    {this.state.showFilters && (
                        <div className="section flex flex-wrap gap-4 p-2">
                            <Select
                                label={t('Role')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.roles.map(r => ({ label: r, value: r.toLowerCase() }))]}
                                value={this.state.filter.role}
                                onChange={(value: string) => this.updateFilter('role', value)}
                                disabled={loading}
                                className="min-w-[150px]"
                            />
                            <Select
                                label={t('Branch')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.branches.map(b => ({ label: b.name, value: String(b.id) }))]}
                                value={this.state.filter.branch}
                                onChange={(value: string) => this.updateFilter('branch', value)}
                                disabled={loading}
                                className="min-w-[170px]"
                            />
                            <Select
                                label={t('Department')}
                                options={[{ label: t('Any'), value: '' }, ...this.state.departments.map(d => ({ label: d.name, value: String(d.id) }))]}
                                value={this.state.filter.department}
                                onChange={(value: string) => this.updateFilter('department', value)}
                                disabled={loading}
                                className="min-w-[170px]"
                            />
                            <Select
                                label={t('Position')}
                                options={[...new Set(this.state.users.map(u => u.title).filter(Boolean))].sort().map(title => ({ label: title, value: title }))}
                                value={this.state.filter.positions}
                                onChange={(value: string[]) => this.setState(prev => ({ filter: { ...prev.filter, positions: value } }), this.applyFilters)}
                                multiple
                                searchable
                                clearable
                                placeholder={t('All Positions')}
                                disabled={loading}
                                className="min-w-[200px]"
                            />
                        </div>
                    )}
                    {loading && (
                        <div className="flex items-center gap-2 text-sm text-muted"><Spinner size="sm" /> {t('Loading employees...')}</div>
                    )}
                    {!loading && visible.length === 0 && (
                        <div className="text-sm text-muted">{t('No employees found')}</div>
                    )}
                    <div className="table-container">
                        <Table className="table-standard table-outline-rows table-compact" aria-label={t('Employees table')}>
                            <TableHeader>
                                <TableRow>
                                    {(['fullName', 'managerName', 'title', 'department'] as const).map((key) => {
                                        const label = key === 'fullName' ? t('Employee Name')
                                            : key === 'managerName' ? t('Reporting To')
                                                : key === 'title' ? t('Position')
                                                    : t('Department');
                                        const isActive = this.state.sortKey === key;
                                        const indicator = isActive ? (this.state.sortDir === 'asc' ? '▲' : '▼') : '';
                                        return (
                                            <TableHead
                                                key={key}
                                                data-testid={`sort-${key}`}
                                                aria-sort={isActive ? (this.state.sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                                                role="columnheader"
                                                tabIndex={0}
                                                onClick={() => this.toggleSort(key)}
                                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.toggleSort(key); } }}
                                                className="cursor-pointer select-none hover:bg-accent"
                                            >
                                                <span className="inline-flex items-center gap-1">
                                                    {label}
                                                    {indicator && <span aria-hidden="true" className="text-xs text-muted-foreground">{indicator}</span>}
                                                </span>
                                            </TableHead>
                                        );
                                    })}
                                    <TableHead>{t('Actions')}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && Array.from({ length: 6 }).map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell colSpan={5}><div className="h-6 w-full animate-pulse rounded bg-muted" /></TableCell>
                                    </TableRow>
                                ))}
                                {!loading && visible.slice(this.state.offset, Math.min(this.state.offset + this.state.count, visible.length)).map((row) => (
                                    <TableRow key={row.fullName}>
                                        <TableCell>
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                                {(this.props.role === Roles.Hr || this.props.role === Roles.Admin) ? (
                                                    <Link to={`/profile/${row.id}/personal`} onClick={() => { this.props.setPrevUrl(this.currentURL); }} className="text-primary font-semibold focus-ring">{row.fullName}</Link>
                                                ) : (<span className="font-semibold">{row.fullName}</span>)}
                                                {/*
                                                  The chip carries the DATE, which is the whole point: "Terminated"
                                                  alone never told anyone whether this person is at their desk today.
                                                  Same chip family as the profile header so the two screens read as
                                                  one system. Rendered only when it says something — an Active row
                                                  stays quiet.
                                                */}
                                                {(() => {
                                                    const state = this.lifecycleOf(row);
                                                    const date = row.exitDate ?? row.lastWorkingDay;
                                                    if (state === 'leaving' && date) {
                                                        return (
                                                            <span className="inline-flex items-center gap-1 rounded-full border border-warning/20 bg-warning/10 px-2 py-0.5 text-xs font-medium text-warning">
                                                                <AppIcon name="CalendarClock" size={12} />
                                                                {t('employees:lifecycle.leavingOn', { date: formatDate(date) })}
                                                            </span>
                                                        );
                                                    }
                                                    if (state === 'former') {
                                                        return (
                                                            <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                                                                <AppIcon name="LogOut" size={12} />
                                                                {date ? t('employees:lifecycle.leftOn', { date: formatDate(date) }) : t('employees:lifecycle.leftNoDate')}
                                                            </span>
                                                        );
                                                    }
                                                    return null;
                                                })()}
                                            </div>
                                        </TableCell>
                                        <TableCell>{this.state.managers.filter(s => s.userId === row.id).map(s => s.managerName.join(', ')).join(', ')}</TableCell>
                                        <TableCell>{row.title}</TableCell>
                                        <TableCell>{row.department ? row.department.name : '-'}</TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-1" dir={this.props.rtl ? 'rtl' : 'ltr'}>
                                                {(AuthRoles.MANAGER.includes(this.props.role)) && (
                                                    <span title={t('Attendance Overview')}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            disabled={loading}
                                                            asChild
                                                            aria-label={t('Attendance Overview')}
                                                        >
                                                            <Link to={`/attendance/overview/${row.id}${row.fullName ? `/${encodeURIComponent(row.fullName)}` : ''}`}>
                                                                <AppIcon name="Calendar" size={16} />
                                                            </Link>
                                                        </Button>
                                                    </span>
                                                )}
                                                {(AuthRoles.MANAGER.includes(this.props.role)) && (
                                                    <span title={t('Leaves & Holidays')}>
                                                        <Button variant="ghost" size="icon" disabled={loading} asChild aria-label={t('Leaves & Holidays')}>
                                                            <Link to={`/profile/${row.id}/leaves`}>
                                                                <AppIcon name="Palmtree" size={16} />
                                                            </Link>
                                                        </Button>
                                                    </span>
                                                )}
                                                {(this.props.role === Roles.Hr || this.props.role === Roles.Admin) && row.id !== this.props.userId && !adminBarredFromReset(this.props.role, row) && (
                                                    <span title={t('Reset password')}>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            aria-label={t('Reset password')}
                                                            disabled={loading}
                                                            onClick={() => this.setState({ resetTarget: { id: row.id, name: row.fullName || row.email || row.id } })}
                                                        >
                                                            <AppIcon name="KeyRound" size={16} />
                                                        </Button>
                                                    </span>
                                                )}
                                                {canAuthorEmployees && (this.props.role === Roles.Hr || this.props.role === Roles.Admin) && (
                                                    checked ? (
                                                        <span title={t('Restore')}>
                                                            <Button variant="ghost" size="icon" aria-label={t('Restore')} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} disabled={isActionLoading(row.id) || this.props.writeAccess.blocked} onClick={async () => {
                                                                if (!canAuthorEmployees) return;
                                                                if (window.confirm(t('Are you sure you want to restore this user'))) {
                                                                    this.setState((s: any) => ({ actionLoadingIds: [...s.actionLoadingIds, row.id] }));
                                                                    try { await restoreUser(row.id); await this.fetchData(); } finally { this.setState((s: any) => ({ actionLoadingIds: s.actionLoadingIds.filter((x: string) => x !== row.id) })); }
                                                                }
                                                            }}>
                                                                {isActionLoading(row.id) ? <Spinner size="sm" /> : <AppIcon name="Undo2" size={16} />}
                                                            </Button>
                                                        </span>
                                                    ) : (
                                                        <span title={t('Archive')}>
                                                            <Button variant="ghost" size="icon" aria-label={t('Archive')} {...(this.props.writeAccess.blocked ? { 'aria-disabled': true, 'aria-describedby': RESTRICTED_WRITE_REASON_ID } : {})} disabled={isActionLoading(row.id) || this.props.writeAccess.blocked} onClick={async () => {
                                                                if (!canAuthorEmployees) return;
                                                                if (window.confirm(t('Are you sure you want to archive this user'))) {
                                                                    this.setState((s: any) => ({ actionLoadingIds: [...s.actionLoadingIds, row.id] }));
                                                                    try { await deleteUser(row.id); await this.fetchData(); } finally { this.setState((s: any) => ({ actionLoadingIds: s.actionLoadingIds.filter((x: string) => x !== row.id) })); }
                                                                }
                                                            }}>
                                                                {isActionLoading(row.id) ? <Spinner size="sm" /> : <AppIcon name="Trash2" size={16} />}
                                                            </Button>
                                                        </span>
                                                    )
                                                )}
                                                {this.props.role === Roles.Admin && (
                                                    <span title={t('End Session')}>
                                                        <Button variant="ghost" size="icon" aria-label={t('End Session')} disabled={loading} onClick={async () => {
                                                            if (window.confirm(t('Are you sure you want to end the session for this user'))) {
                                                                //await revokeAccess(item.id);
                                                            }
                                                        }}>
                                                            <AppIcon name="LogOut" size={16} />
                                                        </Button>
                                                    </span>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <div className="flex justify-end pt-2">
                        <Pagination
                            currentPage={this.state.selectedPageIndex + 1}
                            totalPages={Math.ceil(visible.length / this.state.count) || 1}
                            onPageChange={(newPage) => {
                                const newPageIndex = newPage - 1;
                                this.setState({ selectedPageIndex: newPageIndex, offset: newPageIndex * this.state.count });
                            }}
                        />
                    </div>
                    {this.state.resetTarget && (
                        <ResetPasswordDialog
                            open={this.state.resetTarget !== null}
                            onOpenChange={(open) => { if (!open) this.setState({ resetTarget: null }); }}
                            userId={this.state.resetTarget.id}
                            userDisplayName={this.state.resetTarget.name}
                        />
                    )}
                </section>
            </PageLayout>
        );
    }
}

// HOC to inject navigate into class component
function withNavigation<P extends { navigate?: (path: string) => void }>(Component: React.ComponentType<P>) {
    return function Wrapped(props: Omit<P, 'navigate'>) {
        const navigate = useNavigate();
        return <Component {...props as P} navigate={(path: string) => navigate(path)} />;
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        setPrevUrl: (input: string) => dispatch(setPrevUrl(input))
    }
}
const mapStateToProps = (state: any) => {
    return {
        rtl: state.user.rtl,
        userId: state.user.userId,
        company: state.user.company,
        companyPrimaryColor: state.user.companyPrimaryColor,
        companyName: state.user.companyName,
        role: state.user.role,
    }
}
export default withCompanyCapabilities(withWriteAccess(connect(mapStateToProps, mapDispatchToProps)(withTranslation()(withNavigation(EmployeesGeneral) as any) as any) as any) as any);
